import React, {useState, useEffect} from 'react';
import {DISMISSED_PIN_TO_HOME_PROMPT} from 'utils/store';
import useLocalStorage from 'hooks/useLocalStorage';
import iosBrowserCheck from 'utils/serviceworker/tools/browserCheck.js';

const isIosWebView = iosBrowserCheck() && !window.navigator.standalone;
const chrome = window.navigator.userAgent.match(/Chrome\/(\d+)/);

const WebAppInstallPromptContext = React.createContext();
const REMIND_IN = 1000 * 60 * 60 * 24 * 75; // 75 Days

function WebAppInstallPrompt({children}) {
	const [installPrompt, setInstallPrompt] = useState(null);
	const [wasPromptDismissed, setWasPromptDismissed] = useLocalStorage(
		DISMISSED_PIN_TO_HOME_PROMPT,
		null
	);

	const captureInstallPrompt = event => {
		if (chrome) {
			const chromeVersion = Number(chrome[1]);

			if (chromeVersion <= 67) {
				// Prevent Chrome 67 and earlier from automatically showing the "A2HS banner" prompt: https://developers.google.com/web/updates/2018/06/a2hs-updates
				// Since Chrome 76, calling `event.preventDefault()` will hide the 	mini-info bar: https://developers.google.com/web/fundamentals/app-install-banners
				event.preventDefault();
			}
		}

		// Stash the event so it can be triggered later.
		setInstallPrompt(event);
	};

	const dismissPrompt = () => {
		setWasPromptDismissed(Date.now());
	};

	const promptUser = async () => {
		if (isIosWebView) {
			setWasPromptDismissed(null);
		} else if (installPrompt) {
			await installPrompt.prompt();

			const userChoice = await installPrompt.userChoice;
			if (userChoice.outcome === 'accepted') {
				setInstallPrompt(null);
			}
		}
	};

	useEffect(() => {
		window.addEventListener('beforeinstallprompt', captureInstallPrompt);

		return () => {
			window.removeEventListener(
				'beforeinstallprompt',
				captureInstallPrompt
			);
		};
	}, []);

	// On iOS devices, we always assume 'hasPrompt' to be true.
	// Otherwise, we require the presence of an actual captured &
	// prevented installPrompt event
	const hasPrompt = isIosWebView || installPrompt !== null;

	let capturedPrompt;
	if (hasPrompt) {
		capturedPrompt = {
			isDismissed:
				wasPromptDismissed &&
				+wasPromptDismissed + REMIND_IN > Date.now(),
			type: isIosWebView ? 'iOS' : 'default',
		};
	}

	return (
		<WebAppInstallPromptContext.Provider
			value={{
				capturedPrompt,
				promptUser,
				dismissPrompt,
			}}
		>
			{children}
		</WebAppInstallPromptContext.Provider>
	);
}

export {WebAppInstallPromptContext};

export default WebAppInstallPrompt;
